import { useEffect, useRef } from 'react';

import { BackrsFormAutoComplete, BackrsFormField, CheckboxGroup } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useLazyFetchAutocompleteOptionsQuery } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import { useFetchUserProfileInfoQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import {
  graduationYearOptions,
  programStartYearOptions,
  WHICH_TESTS_VALUES,
  ZIP_PATTERN,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import {
  selectBoosterDraft,
  updateIntake,
  IntakeFields,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import { WhichTests } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, MenuItem, SxProps, TextField, Theme, Typography } from '@mui/material';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';

import { SectionCard } from '../components/SectionCard';
import { intakeRequirements } from '../utils/stepNavigation';

const STRINGS = appStrings.modules.satActBooster;

const SCHOOLS_LOOKUP_ENDPOINT = '/profile/lookups/schools';

const WHICH_TESTS_OPTIONS = WHICH_TESTS_VALUES.map((value) => ({
  value,
  label: STRINGS.whichTestsOptions[value],
}));

/**
 * Standalone intake form: the wizard's first step. Fields persist to the Redux draft. Name/zip come
 * from the user record; the current school and expected graduation year come from the profile query.
 */
export const IntakeStep = () => {
  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectBoosterDraft);
  const { loggedIn } = useAuth();
  const user = useAppSelector(selectUser);

  const {
    data: profileData,
    isLoading: profileLoading,
    isError: isProfileError,
    error: profileError,
  } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, { skip: !loggedIn });

  const profile = profileData?.profile;
  const enrollment = profile?.academicsHighSchoolCurrentEnrollment;
  const profileSchool = typeof enrollment === 'string' ? enrollment : enrollment?.school;
  const profileGradYear = profile?.academicHighSchoolExpectedGraduationYear;
  const profileStartYear = profile?.academicHighSchoolProgramStartYear;

  const [triggerFetchAutocomplete, { data: autocompleteData }] =
    useLazyFetchAutocompleteOptionsQuery();
  const schoolOptions =
    autocompleteData?.find((option) => option.endpoint === SCHOOLS_LOOKUP_ENDPOINT)?.values ?? [];

  // Prefill known profile fields once for authed users so their draft carries existing values.
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (prefilledRef.current || !loggedIn || profileLoading) return;

    prefilledRef.current = true;

    const prefill: IntakeFields = {
      ...(user?.first_name && !draft.firstName ? { firstName: user.first_name } : {}),
      ...(user?.zip_code && !draft.zip ? { zip: user.zip_code } : {}),
      ...(profileSchool && !draft.school ? { school: profileSchool } : {}),
      ...(profileGradYear && draft.expectedGraduationYear === null
        ? { expectedGraduationYear: profileGradYear }
        : {}),
      ...(profileStartYear && draft.programStartYear === null
        ? { programStartYear: Number(profileStartYear) }
        : {}),
    };

    if (Object.keys(prefill).length > 0) dispatch(updateIntake(prefill));
  }, [
    loggedIn,
    profileLoading,
    user,
    profileSchool,
    profileGradYear,
    profileStartYear,
    draft,
    dispatch,
  ]);

  // A failed profile load leaves school and graduation year empty with nothing on screen to fill
  // them, so the submission carries blanks — record it to keep such a run traceable.
  useEffect(() => {
    if (isProfileError) {
      console.error('Failed to load profile for booster intake prefill:', profileError);
    }
  }, [isProfileError, profileError]);

  const asks = intakeRequirements({ loggedIn, userFirstName: user?.first_name });

  // Hold rendering until the profile settles so the authed prefill lands before the form shows.
  if (loggedIn && profileLoading) return null;

  const zipInvalid = draft.zip.length > 0 && !ZIP_PATTERN.test(draft.zip);

  // A program has to finish after it starts, so each year narrows the other's choices.
  const gradYears = graduationYearOptions().filter(
    (year) => draft.programStartYear === null || year > draft.programStartYear,
  );
  const startYears = programStartYearOptions().filter(
    (year) => draft.expectedGraduationYear === null || year < draft.expectedGraduationYear,
  );

  const schoolField: BackrsFormField = {
    name: 'booster-intake-school',
    // Label is rendered above via Typography (matching the other fields), so the input has none.
    label: '',
    placeholder: STRINGS.intake.schoolPlaceholder,
    type: 'text',
    autoCompleteEndpoint: SCHOOLS_LOOKUP_ENDPOINT,
    selectOptions: { type: 'SINGLE', renderAsSelect: true, options: [] },
    onInputChange: (input: string) =>
      triggerFetchAutocomplete({ endpoint: SCHOOLS_LOOKUP_ENDPOINT, searchTerm: input }),
  };

  const schoolControllerField = {
    name: 'booster-intake-school',
    value: draft.school,
    onChange: (value: string) => dispatch(updateIntake({ school: value ?? '' })),
    onBlur: () => undefined,
    ref: () => undefined,
  } as unknown as ControllerRenderProps<FieldValues, string>;

  return (
    <SectionCard>
      <Box sx={headerSx}>
        <Typography variant="text-lg-semibold" sx={titleSx}>
          {STRINGS.intake.title}
        </Typography>
        <Typography variant="text-sm-regular" sx={bodySx}>
          {STRINGS.intake.body}
        </Typography>
      </Box>

      <Box sx={fieldsSx}>
        {asks.firstName && (
          <Box sx={fieldSx}>
            <Typography variant="text-sm-semibold" sx={fieldLabelSx}>
              {STRINGS.intake.firstNameLabel}
            </Typography>
            <TextField
              id="booster-intake-first-name"
              placeholder={STRINGS.intake.firstNamePlaceholder}
              value={draft.firstName}
              onChange={(event) => dispatch(updateIntake({ firstName: event.target.value }))}
              inputProps={{
                autoComplete: 'given-name',
                'aria-label': STRINGS.intake.firstNameLabel,
              }}
              fullWidth
            />
          </Box>
        )}

        {asks.zip && (
          <Box sx={fieldSx}>
            <Typography variant="text-sm-semibold" sx={fieldLabelSx}>
              {STRINGS.intake.zipLabel}
            </Typography>
            <TextField
              id="booster-intake-zip"
              placeholder={STRINGS.intake.zipPlaceholder}
              value={draft.zip}
              onChange={(event) =>
                dispatch(updateIntake({ zip: event.target.value.replace(/\D/g, '').slice(0, 5) }))
              }
              error={zipInvalid}
              helperText={zipInvalid ? STRINGS.intake.zipError : undefined}
              inputProps={{
                inputMode: 'numeric',
                autoComplete: 'postal-code',
                'aria-label': STRINGS.intake.zipLabel,
              }}
              fullWidth
            />
          </Box>
        )}

        {asks.school && (
          <Box sx={fieldSx}>
            <Typography variant="text-sm-semibold" sx={fieldLabelSx}>
              {STRINGS.intake.schoolLabel}
            </Typography>
            <BackrsFormAutoComplete
              isMultiple={false}
              autoCompleteOptions={schoolOptions}
              options={[]}
              field={schoolField}
              controllerField={schoolControllerField}
              groupedFields={{}}
              index={0}
              error={undefined}
              groupKey="booster-intake-fields"
            />
          </Box>
        )}

        {asks.expectedGraduationYear && (
          <Box sx={fieldSx}>
            <Typography variant="text-sm-semibold" sx={fieldLabelSx}>
              {STRINGS.intake.gradYearLabel}
            </Typography>
            <TextField
              id="booster-intake-grad-year"
              value={
                draft.expectedGraduationYear === null ? '' : String(draft.expectedGraduationYear)
              }
              onChange={(event) =>
                dispatch(updateIntake({ expectedGraduationYear: Number(event.target.value) }))
              }
              select
              fullWidth
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem id="booster-intake-grad-year-placeholder" value="" disabled>
                {STRINGS.intake.gradYearPlaceholder}
              </MenuItem>
              {gradYears.map((year) => (
                <MenuItem key={year} value={String(year)} id={`booster-intake-grad-year-${year}`}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {asks.programStart && (
          <Box sx={fieldSx}>
            <Typography variant="text-sm-semibold" sx={fieldLabelSx}>
              {STRINGS.intake.startYearLabel}
            </Typography>
            <TextField
              id="booster-intake-start-year"
              value={draft.programStartYear === null ? '' : String(draft.programStartYear)}
              onChange={(event) =>
                dispatch(updateIntake({ programStartYear: Number(event.target.value) }))
              }
              select
              fullWidth
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem id="booster-intake-start-year-placeholder" value="" disabled>
                {STRINGS.intake.startYearPlaceholder}
              </MenuItem>
              {startYears.map((year) => (
                <MenuItem key={year} value={String(year)} id={`booster-intake-start-year-${year}`}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        <Box sx={fieldSx}>
          <Typography variant="text-sm-semibold" sx={fieldLabelSx}>
            {STRINGS.intake.whichTestsLabel}
          </Typography>
          <CheckboxGroup
            id="booster-which-tests"
            options={WHICH_TESTS_OPTIONS}
            selectedValues={draft.whichTests ? [draft.whichTests] : []}
            setSelectedValues={(values) => {
              // Single-select: keep the newly toggled value; unchecking the only choice is a no-op,
              // since clearing the answer would discard every score entered under it.
              const next = values.find((value) => value !== draft.whichTests);

              if (next) dispatch(updateIntake({ whichTests: next as WhichTests }));
            }}
          />
        </Box>
      </Box>
    </SectionCard>
  );
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const fieldsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  maxWidth: backrs.width['width-sm'],
});

const fieldSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const fieldLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});
